import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  Play,
  Mic,
  Headphones,
  User,
  Database,
  Mountain,
  Coins,
  Droplets,
  Network,
  Layers,
  Landmark,
  GraduationCap,
  Linkedin,
} from 'lucide-react';
import { colors } from '../styles/colors';
import { BTX_REFERRAL_URL } from '../config/links';

const SPOTIFY_SHOW = 'https://open.spotify.com/show/7qhMnFWrSkCzf4MAFIwgmn?si=e5c37a78c42c40c0';
const LINKEDIN_URL = 'https://linkedin.com/company/arizore';

// ---------------------------------------------------------------------------
// CONTENT MODEL — populate `spotifyUrl`, `thumbnail`, `title`, `summary`
// as final assets / links arrive. Cards gracefully render swap-ready
// placeholders whenever a field is null (no dummy links, no fabricated copy).
// ---------------------------------------------------------------------------

// Featured Episode (George × Marcus — flagship Episode 03) — only real episode + thumbnail today
const FEATURED = {
  spotifyEpisodeId: '4Np1yHZlajbESx0AtrXmkv',
  guests: 'George Boot × Marcus Grubb',
  series: 'The Reserve Room · Episode 03',
  title: 'Gold ETFs, Tokenisation & The Future Of Financial Markets',
  summary:
    'A discussion exploring gold ETFs, institutional finance, tokenisation infrastructure, liquidity, and the future evolution of global capital markets. Marcus brings experience from JPMorgan, the World Gold Council, State Street, and multiple digital asset ventures.',
  thumbnail: '/reserve-room-ep3-thumbnail.webp',
};
const FEATURED_EPISODE_URL = `https://open.spotify.com/episode/${FEATURED.spotifyEpisodeId}`;
const FEATURED_EMBED_URL = `https://open.spotify.com/embed/episode/${FEATURED.spotifyEpisodeId}/video?utm_source=generator`;

const STATUS_STYLES = {
  live: { label: 'Live', color: colors.teal, bg: `${colors.teal}1A`, border: `${colors.teal}40`, dot: true },
  production: { label: 'In Production', color: '#F2D675', bg: '#F2D6751A', border: '#F2D67540', dot: true },
  soon: { label: 'Coming Soon', color: 'rgba(255,255,255,0.55)', bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.12)', dot: false },
};

const StatusPill = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.soon;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.14em]"
      style={{ backgroundColor: s.bg, color: s.color, border: `1px solid ${s.border}` }}
    >
      {s.dot && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.color }} />}
      {s.label}
    </span>
  );
};

const SectionHeading = ({ eyebrow, title, sub, align = 'center', maxW = 'max-w-3xl' }) => (
  <div className={`${maxW} ${align === 'center' ? 'mx-auto text-center' : ''} mb-12 sm:mb-16`}>
    {eyebrow && (
      <div className={`flex items-center gap-3 mb-4 ${align === 'center' ? 'justify-center' : ''}`}>
        <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />
        <span className="text-[11px] uppercase tracking-[0.28em] font-medium" style={{ color: colors.teal }}>{eyebrow}</span>
        {align === 'center' && <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />}
      </div>
    )}
    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight leading-tight" style={{ color: colors.white }}>{title}</h2>
    {sub && <p className="text-base sm:text-lg leading-relaxed" style={{ color: colors.white, opacity: 0.65 }}>{sub}</p>}
  </div>
);

const MediaPlaceholder = ({ Icon = Play, label = 'Artwork coming soon', ratio = 'aspect-video', rounded = 'rounded-xl', bg = '/ra-bg-3.webp' }) => (
  <div
    className={`relative ${ratio} ${rounded} overflow-hidden flex items-center justify-center`}
    style={{ background: 'linear-gradient(135deg, rgba(0,128,128,0.10) 0%, rgba(255,255,255,0.02) 100%)', border: '1px solid rgba(255,255,255,0.08)' }}
  >
    <div aria-hidden="true" className="absolute inset-0" style={{ backgroundImage: `url(${bg})`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.07 }} />
    <div className="relative flex flex-col items-center gap-2 px-3 text-center">
      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${colors.teal}18`, border: `1px solid ${colors.teal}40` }}>
        <Icon className="w-5 h-5" style={{ color: colors.teal }} strokeWidth={1.5} />
      </div>
      <span className="text-[10px] uppercase tracking-[0.22em]" style={{ color: colors.white, opacity: 0.4 }}>{label}</span>
    </div>
  </div>
);

// Renders a real thumbnail image when provided, otherwise a swap-ready placeholder
const EpisodeThumb = ({ thumbnail, alt, Icon = Headphones, label = 'Thumbnail coming soon', ratio = 'aspect-video', bg = '/ra-bg-3.webp', rounded = 'rounded-xl' }) =>
  thumbnail ? (
    <div className={`relative ${ratio} ${rounded} overflow-hidden`} style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
      <img src={thumbnail} alt={alt} className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />
    </div>
  ) : (
    <MediaPlaceholder Icon={Icon} label={label} ratio={ratio} rounded={rounded} bg={bg} />
  );

const CategoryBadge = ({ label }) => (
  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ backgroundColor: `${colors.teal}14`, color: colors.teal, border: `1px solid ${colors.teal}30` }}>
    {label}
  </span>
);

const ReserveRoomPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    const raf = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // §3 Latest Conversations (Reserve Room episodes — flagship first)
  const latestConversations = [
    {
      id: 'caroline-reserve-matters',
      guest: 'Caroline Macdonald',
      title: 'Why The Reserve Matters More Than The Token',
      status: 'live',
      thumbnail: '/reserve-room-ep4-thumbnail.webp',
      spotifyUrl: 'https://open.spotify.com/episode/32mspPKWFhq1ljwSjTSDQF',
    },
    {
      id: 'marcus-grubb',
      guest: 'Marcus Grubb',
      title: 'Gold ETFs, Tokenisation & The Future Of Financial Markets',
      status: 'live',
      thumbnail: '/reserve-room-ep3-thumbnail.webp',
      spotifyUrl: FEATURED_EPISODE_URL,
    },
    {
      id: 'caroline-macdonald',
      guest: 'Caroline Macdonald',
      title: 'From Crypto Hype To Financial Infrastructure',
      status: 'live',
      thumbnail: '/reserve-room-ep2-thumbnail.webp',
      spotifyUrl: 'https://open.spotify.com/episode/40I8kdg7qb55RTIcAr0tWV',
    },
  ];

  // §4 industry insights — real existing articles
  const insights = [
    { title: 'Live Reserve Update', category: 'Reserves', excerpt: 'Arizore confirms $50.1M live reserve as AZG and AZS mint completes on-chain.', href: '/reserve-room/written/live-reserve', image: '/insight-live-reserve.webp' },
    { title: 'Understanding Arizore', category: 'Ecosystem', excerpt: 'Bridging physical assets with digital finance through disciplined reserve infrastructure.', href: '/reserve-room/written/understanding-arizore', image: '/insight-understanding-arizore.webp' },
    { title: 'Overcoming Capital Access Barriers in Mining', category: 'Mining', excerpt: 'How structured reserve infrastructure turns verified mining assets into transparent, institution-ready collateral.', href: '/reserve-room/written/overcoming-capital-access-barriers', image: '/insight-overcoming-capital.webp' },
  ];

  // §5 five core themes
  const themes = [
    { Icon: Database, title: 'Reserves', body: 'Reserve frameworks, transparency, reporting, and participation.' },
    { Icon: Mountain, title: 'Mining', body: 'Resource development, project evaluation, and capital challenges.' },
    { Icon: Coins, title: 'Bullion', body: 'Physical assets, reserve contribution, and precious metals.' },
    { Icon: Droplets, title: 'Liquidity', body: 'Collateral, capital access, and structured participation.' },
    { Icon: Network, title: 'Ecosystem', body: 'Industry leaders, partners, and participant conversations.' },
  ];

  // §8 continue exploring
  const ecosystem = [
    { Icon: Landmark, title: 'Liquidity Providers', href: '/liquidity-providers' },
    { Icon: Mountain, title: 'Mines', href: '/mines' },
    { Icon: Coins, title: 'Bullion Providers', href: '/bullion-providers' },
    { Icon: Layers, title: 'Reserve Architecture', href: '/reserve-architecture' },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#050505' }}>
      {/* §1 HERO */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #000000 0%, #03090C 55%, #07141A 100%)' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/ra-bg-1.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.12 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(60% 60% at 30% 40%, rgba(0,128,128,0.12) 0%, rgba(0,0,0,0) 70%)' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 sm:pt-40 sm:pb-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: copy */}
            <div className={`transition-all duration-1000 ease-out transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10" style={{ backgroundColor: colors.teal, opacity: 0.7 }} />
                <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] font-medium" style={{ color: colors.teal }}>
                  <Mic className="w-3.5 h-3.5" strokeWidth={1.5} />
                  The Reserve Room
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-[-0.02em] mb-6 font-bold" style={{ color: colors.white }}>
                Conversations Around Assets, Liquidity &amp; Reserves
              </h1>
              <p className="text-base sm:text-lg lg:text-xl leading-[1.55] max-w-xl mb-10" style={{ color: 'rgba(255,255,255,0.70)' }}>
                Industry conversations and perspectives on assets, liquidity, reserves, mining, bullion, and infrastructure — from across the Arizore ecosystem.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <a
                  href={FEATURED_EPISODE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="rr-hero-watch-latest-btn"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
                  style={{ backgroundColor: colors.teal, color: colors.white, border: `1px solid ${colors.teal}`, textDecoration: 'none' }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.teal}26`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <Play className="w-4 h-4" fill="currentColor" />
                  Listen To Featured Conversation
                </a>
                <button
                  onClick={() => scrollTo('industry-insights')}
                  data-testid="rr-hero-browse-insights-btn"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
                  style={{ backgroundColor: 'transparent', color: colors.white, border: '1px solid rgba(255,255,255,0.25)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
                >
                  Browse Insights
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Right: featured artwork (real thumbnail) */}
            <div className={`transition-all duration-1000 ease-out transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '150ms' }}>
              <a
                href={FEATURED_EPISODE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="rr-hero-featured-artwork"
                className="group relative block rounded-2xl overflow-hidden"
                style={{ border: `1px solid ${colors.teal}26`, boxShadow: `0 40px 80px -40px ${colors.teal}40` }}
              >
                <div className="aspect-video relative">
                  <picture>
                    <source srcSet="/reserve-room-ep3-thumbnail.webp" type="image/webp" />
                    <img
                      src="/reserve-room-ep3-thumbnail.jpg"
                      alt={FEATURED.title}
                      data-testid="rr-hero-featured-thumbnail"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.02]"
                      loading="lazy"
                      decoding="async"
                    />
                  </picture>
                  <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.55) 100%)' }} />
                  <div className="absolute top-4 right-4"><CategoryBadge label="Featured" /></div>
                  <div aria-hidden="true" className="absolute inset-0 flex items-center justify-center">
                    <div className="flex items-center justify-center rounded-full transition-all duration-500 group-hover:scale-110" style={{ width: 68, height: 68, backgroundColor: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.4)', backdropFilter: 'blur(8px)' }}>
                      <Play className="w-6 h-6" style={{ color: colors.white, marginLeft: 3 }} fill={colors.white} strokeWidth={1.5} />
                    </div>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* §2 FEATURED EPISODE */}
      <section className="py-20 sm:py-28" style={{ backgroundColor: '#040404' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Featured Conversation"
            title="A highlighted discussion from The Reserve Room"
          />
          <div className="rounded-2xl overflow-hidden max-w-3xl mx-auto" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)', border: `1px solid ${colors.teal}30`, boxShadow: `0 40px 80px -50px ${colors.teal}40` }}>
            {/* Details + Spotify embed only */}
            <div className="p-7 sm:p-10 flex flex-col">
              <div className="flex items-center gap-3 mb-4">
                <StatusPill status="live" />
                <span className="text-[11px] uppercase tracking-[0.22em]" style={{ color: colors.teal, opacity: 0.85 }}>{FEATURED.series}</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold mb-3 tracking-tight leading-tight" style={{ color: colors.white }}>
                {FEATURED.title}
              </h3>
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${colors.teal}18`, border: `1px solid ${colors.teal}40` }}>
                  <User className="w-4 h-4" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <span className="text-sm font-medium" style={{ color: colors.white, opacity: 0.8 }}>{FEATURED.guests}</span>
              </div>
              <p className="text-sm sm:text-base leading-relaxed mb-6" style={{ color: colors.white, opacity: 0.65 }}>
                {FEATURED.summary}
              </p>

              {/* Embedded Spotify player */}
              <div className="mb-6 rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                <iframe
                  data-testid="rr-featured-spotify-embed"
                  title="Featured episode — Spotify player"
                  src={FEATURED_EMBED_URL}
                  width="100%"
                  height="351"
                  frameBorder="0"
                  loading="lazy"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  style={{ display: 'block', border: 0 }}
                />
              </div>

              <a
                href={FEATURED_EPISODE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="rr-featured-listen-now-btn"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg text-sm font-semibold uppercase tracking-[0.14em] transition-all duration-300 self-start"
                style={{ backgroundColor: colors.teal, color: colors.white, textDecoration: 'none' }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.teal}26`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <Play className="w-4 h-4" fill="currentColor" />
                Listen On Spotify
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* §3 LATEST CONVERSATIONS */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#050505' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/ra-bg-3.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.06 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #050505 0%, rgba(5,5,5,0.7) 50%, #050505 100%)' }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Latest Conversations"
            title="SELECTED CONVERSATIONS"
            sub="A small, curated set of high-quality conversations from across the reserve ecosystem."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {latestConversations.map((ep) => {
              const hasLink = Boolean(ep.spotifyUrl);
              const CardTag = hasLink ? 'a' : 'div';
              const cardProps = hasLink
                ? { href: ep.spotifyUrl, target: '_blank', rel: 'noopener noreferrer' }
                : {};
              return (
                <CardTag
                  key={ep.id}
                  {...cardProps}
                  data-testid={`rr-conversation-${ep.id}`}
                  className={`group rounded-2xl overflow-hidden transition-all duration-500 block ${hasLink ? 'cursor-pointer' : ''}`}
                  style={{
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${colors.teal}55`; e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 22px 44px -22px ${colors.teal}40`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div className="p-3">
                    <EpisodeThumb thumbnail={ep.thumbnail} alt={ep.title} Icon={Headphones} label="Thumbnail coming soon" ratio="aspect-video" />
                  </div>
                  <div className="px-5 pb-6 pt-1">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <StatusPill status={ep.status} />
                      <span className="text-[10px] uppercase tracking-[0.16em] font-medium truncate" style={{ color: colors.teal }}>{ep.guest}</span>
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold mb-4 leading-snug" style={{ color: colors.white }}>{ep.title}</h3>
                    {hasLink ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: colors.teal }}>
                        Listen On Spotify
                        <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.14em]" style={{ color: colors.white, opacity: 0.4 }}>
                        <Headphones className="w-3.5 h-3.5" strokeWidth={1.5} />
                        Spotify Link Coming Soon
                      </span>
                    )}
                  </div>
                </CardTag>
              );
            })}
          </div>
        </div>
      </section>

      {/* §4 INDUSTRY INSIGHTS */}
      <section id="industry-insights" className="py-20 sm:py-28" style={{ backgroundColor: '#050505' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Editorial"
            title="INDUSTRY INSIGHTS"
            sub="Short-form commentary, educational content, and reserve intelligence designed to help participants better understand the ecosystem."
          />
          <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
            {insights.map((a) => (
              <a
                key={a.href}
                href={a.href}
                data-testid={`rr-insight-${a.href.split('/').pop()}`}
                className="group rounded-2xl overflow-hidden transition-all duration-500 flex flex-col"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.08)', textDecoration: 'none' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${colors.teal}45`; e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 22px 44px -22px ${colors.teal}40`; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div className="p-3">
                  <div className="relative aspect-[16/9] rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                    <img src={a.image} alt={a.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" loading="lazy" decoding="async" />
                  </div>
                </div>
                <div className="px-6 pb-6 pt-1 flex flex-col flex-grow">
                  <div className="mb-3"><CategoryBadge label={a.category} /></div>
                  <h3 className="text-lg font-semibold mb-2.5 leading-snug" style={{ color: colors.white }}>{a.title}</h3>
                  <p className="text-sm leading-relaxed flex-grow" style={{ color: colors.white, opacity: 0.65 }}>{a.excerpt}</p>
                  <span className="inline-flex items-center gap-1.5 mt-5 text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: colors.teal }}>
                    Read Insight
                    <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* §5 FIVE CORE THEMES */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ background: 'linear-gradient(180deg, #050505 0%, #08161B 50%, #050505 100%)' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(60% 50% at 50% 30%, rgba(0,128,128,0.10) 0%, rgba(0,0,0,0) 70%)' }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Coverage"
            title="BUILT AROUND FIVE CORE THEMES"
            sub="Every conversation and insight maps to the themes shaping the reserve ecosystem."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-5">
            {themes.map(({ Icon, title, body }) => (
              <div
                key={title}
                data-testid={`rr-theme-${title.toLowerCase()}`}
                className="flex flex-col p-6 sm:p-7 rounded-2xl"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${colors.teal}14`, border: `1px solid ${colors.teal}40` }}>
                  <Icon className="w-6 h-6" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <h3 className="text-base font-semibold uppercase tracking-[0.08em] mb-2" style={{ color: colors.white }}>{title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: colors.white, opacity: 0.62 }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* §7 WHY THE RESERVE ROOM EXISTS */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#050505' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/ra-bg-1.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.08 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #050505 0%, rgba(5,5,5,0.7) 50%, #050505 100%)' }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionHeading
            eyebrow="Why It Exists"
            title="BETTER CONVERSATIONS CREATE BETTER UNDERSTANDING"
            sub="The Reserve Room was created to encourage thoughtful discussions around assets, liquidity, reserves, and participation. The goal is to make complex topics easier to understand while promoting transparency and education across the ecosystem."
          />
          <div className="max-w-2xl mx-auto rounded-2xl p-8 sm:p-10" style={{ background: 'linear-gradient(180deg, rgba(0,128,128,0.10) 0%, rgba(0,128,128,0.03) 100%)', border: `1px solid ${colors.teal}40`, boxShadow: `0 30px 60px -30px ${colors.teal}40` }}>
            <GraduationCap className="w-7 h-7 mx-auto mb-4" style={{ color: colors.teal }} strokeWidth={1.5} />
            <p className="text-xl sm:text-2xl font-bold leading-snug mb-2" style={{ color: colors.white }}>
              This is not a promotional platform.
            </p>
            <p className="text-xl sm:text-2xl font-bold leading-snug" style={{ color: colors.teal }}>
              It is an educational platform.
            </p>
          </div>
        </div>
      </section>

      {/* §8 CONTINUE EXPLORING */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#040404' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/ra-bg-2.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.07 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #040404 0%, rgba(4,4,4,0.7) 50%, #040404 100%)' }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Continue Exploring"
            title="DISCOVER THE WIDER ECOSYSTEM"
            sub="Explore how reserves, participants, and liquidity connect through the Arizore framework."
          />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 mb-10">
            {ecosystem.map(({ Icon, title, href }) => (
              <button
                key={title}
                onClick={() => navigate(href)}
                data-testid={`rr-explore-${title.toLowerCase().replace(/\s+/g, '-')}`}
                className="group flex flex-col items-start text-left p-6 sm:p-7 rounded-2xl transition-all duration-500"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.08)' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${colors.teal}45`; e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 22px 44px -22px ${colors.teal}40`; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${colors.teal}14`, border: `1px solid ${colors.teal}40` }}>
                  <Icon className="w-6 h-6" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <h3 className="text-base sm:text-lg font-semibold mb-3 tracking-tight leading-tight" style={{ color: colors.white }}>{title}</h3>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: colors.teal }}>
                  Learn More
                  <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </button>
            ))}
          </div>
          <div className="flex justify-center">
            <a
              href={BTX_REFERRAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="rr-explore-btx-btn"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-sm font-semibold uppercase tracking-[0.14em] transition-all duration-300"
              style={{ backgroundColor: 'transparent', color: colors.white, border: '1px solid rgba(255,255,255,0.25)', textDecoration: 'none' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
            >
              Visit BTX Marketplace
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* §9 FOLLOW THE RESERVE ROOM */}
      <section className="py-20 sm:py-28" style={{ backgroundColor: '#050505' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionHeading
            eyebrow="Stay Connected"
            title="FOLLOW THE RESERVE ROOM"
            sub="Follow The Reserve Room for new episodes, insights, and industry conversations."
          />
          <div className="flex items-center justify-center gap-4 sm:gap-5 mb-10">
            <a
              href={SPOTIFY_SHOW}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="rr-social-spotify"
              aria-label="Spotify"
              className="w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-300"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1DB954'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <Headphones className="w-6 h-6" style={{ color: '#1DB954' }} strokeWidth={1.5} />
            </a>
            <a
              href={LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="rr-social-linkedin"
              aria-label="LinkedIn"
              className="w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-300"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#0A66C2'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <Linkedin className="w-6 h-6" style={{ color: '#0A66C2' }} strokeWidth={1.5} />
            </a>
          </div>
          <a
            href={SPOTIFY_SHOW}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="rr-follow-btn"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
            style={{ backgroundColor: colors.teal, color: colors.white, textDecoration: 'none' }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.teal}26`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <Mic className="w-4 h-4" />
            Follow The Reserve Room
          </a>
        </div>
      </section>
    </div>
  );
};

export default ReserveRoomPage;
